import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../stores/themeStore';
import { useLanguageStore } from '../../stores/languageStore';
import { useNoAiStore } from '../../stores/noAiStore';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { useOnboardingReactivationStore } from '../../stores/onboardingReactivationStore';
import { isCustomEmailTemplateEnabled, isDevToolsEnabled } from '../../env';
import { Button, buttonClassName } from '../../ui/Button/Button';
import { RadioGroup } from '../../ui/Radio/RadioGroup';
import { Switch } from '../../ui/Switch/Switch';
import { PageTitle } from '../../ui/PageTitle/PageTitle';
import { SyncType } from '../../types/sync/enums';
import { useSync } from '../../hooks/useSync';
import { useFullSyncOnHome } from '../../hooks/useFullSyncOnHome';
import { TranslationKey } from '../../i18n/translationKeys';
import { SyncStartDatePanel } from './SyncStartDate/SyncStartDatePanel';
import { OnboardingReactivationPanel } from './OnboardingReactivation/OnboardingReactivationPanel';
import { PortalAutoOpenPanel } from './PortalAutoOpen/PortalAutoOpenPanel';
import { BccEmailPanel } from './BccEmail/BccEmailPanel';
import { CustomEmailTemplatePanel } from './CustomEmailTemplate/CustomEmailTemplatePanel';
import { ReplyToEmailPanel } from './ReplyToEmail/ReplyToEmailPanel';
import { RelanceMinimumAmountPanel } from './RelanceMinimumAmount/RelanceMinimumAmountPanel';
import { RelanceFollowUpDaysPanel } from './RelanceFollowUpDays/RelanceFollowUpDaysPanel';

export const Settings: React.FC = () => {
    const { t } = useTranslation();
    const { dark, setDark } = useThemeStore();
    const { lang, setLang } = useLanguageStore();
    const { noAi, toggle: toggleNoAi } = useNoAiStore();
    // The Sync Start Date only bounds the Acomba (bridge) sync; the AcceoConnect path ignores it, so
    // the panel is shown only for Acomba tenants.
    const isAcomba = useIsAcomba();
    const fetchStatus = useOnboardingReactivationStore(s => s.fetchStatus);
    const [hasRelanceAccess, setHasRelanceAccess] = useState(false);
    useEffect(() => {
        fetchStatus().then(s => setHasRelanceAccess(s.hasRelanceAccess)).catch(() => setHasRelanceAccess(false));
    }, [fetchStatus]);
    const [syncType, setSyncType] = useState(SyncType.Full);
    // Integration-aware sync (Acomba bridge or AcceoConnect) — shared with the header refresh button.
    const { syncing, syncStatus, canSync } = useSync();
    const syncOnHome = useFullSyncOnHome();

    return (
        <div className="max-w-4xl mx-auto flex flex-col gap-3xl">
            <PageTitle>{t('settings.title')}</PageTitle>
            <div className="space-y-m">
                <h2 className="text-block-title-emphasized text-content">{t(TranslationKey.Settings.UserSection)}</h2>
                <div className="rounded-lg border border-line p-l space-y-xl">
                    {/* Theme Toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-block-title text-content">{t(TranslationKey.Settings.Theme)}</h2>
                            <p className="text-primary-700">{t(TranslationKey.Settings.ThemeDescription)}</p>
                        </div>
                        <Switch
                            testId="settings-theme-toggle"
                            checked={dark}
                            onChange={setDark}
                            label={t(TranslationKey.Settings.Theme)}
                        />
                    </div>

                    <hr className="border-line-subtle" />

                    {/* Language Selector */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-block-title text-content">{t('settings.language')}</h2>
                            <p className="text-primary-700">{t('settings.languageDescription')}</p>
                        </div>
                        {/* Filled marks the active choice, bordered the alternative; aria-pressed is what
                            carries that to assistive tech, since neither variant conveys state on its own. */}
                        <div className="flex gap-s">
                            <Button
                                data-testid="settings-lang-fr"
                                onClick={() => setLang('fr')}
                                variant={lang === 'fr' ? 'primary' : 'secondary'}
                                aria-pressed={lang === 'fr'}
                            >
                                Français
                            </Button>
                            <Button
                                data-testid="settings-lang-en"
                                onClick={() => setLang('en')}
                                variant={lang === 'en' ? 'primary' : 'secondary'}
                                aria-pressed={lang === 'en'}
                            >
                                English
                            </Button>
                        </div>
                    </div>

                    <PortalAutoOpenPanel />

                    <OnboardingReactivationPanel />
                </div>
            </div>

            {(isAcomba || hasRelanceAccess) && (
                <div className="space-y-m">
                    <h2 className="text-block-title-emphasized text-content">{t(TranslationKey.Settings.CompanySection)}</h2>
                    <div className="rounded-lg border border-line p-l space-y-xl">
                        {hasRelanceAccess && <ReplyToEmailPanel />}
                        {hasRelanceAccess && isAcomba && <hr className="border-line-subtle" />}
                        {/* Sync Start Date — Acomba only; adjustable anytime, takes effect on the next sync. */}
                        {isAcomba && <SyncStartDatePanel />}
                        {hasRelanceAccess && <hr className="border-line-subtle" />}
                        {hasRelanceAccess && <BccEmailPanel />}
                        {hasRelanceAccess && <hr className="border-line-subtle" />}
                        {hasRelanceAccess && isCustomEmailTemplateEnabled && <CustomEmailTemplatePanel />}
                        {hasRelanceAccess && isCustomEmailTemplateEnabled && <hr className="border-line-subtle" />}
                        {hasRelanceAccess && <RelanceMinimumAmountPanel />}
                        {hasRelanceAccess && <hr className="border-line-subtle" />}
                        {hasRelanceAccess && <RelanceFollowUpDaysPanel />}
                    </div>
                </div>
            )}

            {isDevToolsEnabled && (
                <div className="space-y-m">
                    <h2 className="text-block-title-emphasized text-content">{t(TranslationKey.Settings.DevSection)}</h2>
                    <div className="rounded-lg border border-line p-l space-y-xl">
                        {/* Dev: No-AI Mode */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-block-title text-content">{t(TranslationKey.Settings.NoAiMode)}</h2>
                                <p className="text-primary-700">{t(TranslationKey.Settings.NoAiModeDescription)}</p>
                            </div>
                            <Switch
                                testId="settings-noai-toggle"
                                checked={noAi}
                                onChange={toggleNoAi}
                                label={t(TranslationKey.Settings.NoAiMode)}
                            />
                        </div>

                        <hr className="border-line-subtle" />

                        {/* Dev: Connect Sync */}
                        <div className="flex items-start justify-between gap-l">
                            <div className="flex-1">
                                <h2 className="text-block-title text-content">{t(TranslationKey.Settings.SyncTitle)}</h2>
                                <p data-testid="settings-sync-status" className="text-primary-700">
                                    {syncStatus ?? t(TranslationKey.Settings.SyncDescription)}
                                </p>
                                {/* Offered for both integrations. On Acomba the choice is not how much gets
                                    read — both modes read the whole company, since an incremental read cannot
                                    notice a destroyed AR record — but how much gets recomputed: Full rebuilds
                                    every relance and its AI output, Partial keeps the ones whose input did not
                                    change. */}
                                <RadioGroup
                                    name="settings-sync-type"
                                    className="mt-m"
                                    value={syncType}
                                    onChange={value => setSyncType(value as SyncType)}
                                    disabled={syncing}
                                    options={[
                                        { value: SyncType.Full, label: t(TranslationKey.Settings.SyncFull), 'data-testid': 'settings-sync-type-full' },
                                        { value: SyncType.Partial, label: t(TranslationKey.Settings.SyncPartial), 'data-testid': 'settings-sync-type-partial' },
                                    ]}
                                />
                            </div>

                            <Button data-testid="settings-sync-btn" onClick={() => syncOnHome(syncType)} disabled={syncing || !canSync}>
                                {syncing ? t(TranslationKey.Settings.SyncButtonRunning) : t(TranslationKey.Settings.SyncButtonIdle)}
                            </Button>
                        </div>

                        <hr className="border-line-subtle" />

                        {/* Dev: Priority Ranking Preview */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-block-title text-content">{t(TranslationKey.DevRanking.Title)}</h2>
                            </div>
                            <Link
                                to="/dev/ranking"
                                data-testid="settings-dev-ranking-link"
                                className={buttonClassName('secondary')}
                            >
                                {t(TranslationKey.DevRanking.Title)}
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
